import { Component, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Observable, Subject, debounceTime, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DynamicFormComponent } from '../../../../shared/dynamic-form/dynamic-form.component';
import { FieldOptions } from '../../../../shared/dynamic-form/interfaces/field-options.interface';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { MaritalStatus } from '../../../../shared/models/enums/marital-status.enum';
import { Nationality, labelsByNationality } from '../../../../shared/models/enums/nationality.enum';
import { ResidentialStatus, ResidentialStatusLabel } from '../../../../shared/models/enums/residential-status.enum';
import { Title } from '../../../../shared/models/enums/title.enum';
import { TownDto } from '../../../../shared/models/town.dto';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { BeneficiaryEmploymentService } from '../../../../shared/services/beneficiary-employment.service';
import { BeneficiaryComplementsService } from '../../../../shared/services/beneficiaryComplements.service';
import { DegreeService } from '../../../../shared/services/degree.service';
import { JobService } from '../../../../shared/services/job.service';
import { QPVService } from '../../../../shared/services/qpv.service.ts';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { StructureService } from '../../../../shared/services/structure.service';
import { TownService } from '../../../../shared/services/town.service';
import { TrainingAreaService } from '../../../../shared/services/trainingArea.service';
import { TransportModeService } from '../../../../shared/services/transport-mode.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryComplement } from '../../../beneficiary-details/complement/beneficiaryComplement';
import { BeneficiaryEmploymentDto } from '../../../beneficiary-details/employment/dto/beneficiary-employment.dto';
import { JobCategoryDto } from '../../../beneficiary-details/employment/dto/job-category.dto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { FseDetailInitialData, FseDetailsComponent } from '../../fse/fse-details/fse-details.component';
import { AssessmentSignatureComponent } from '../assessment-signature/assessment-signature.component';
import {
  AssessmentActivity,
  AssessmentChildAge,
  AssessmentChildSituation,
  AssessmentContext,
  AssessmentContractTypes,
  AssessmentExperienceLength,
  AssessmentHowLongHasWorked,
  AssessmentJobCenterRegistration,
  AssessmentProExpDuration,
  AssessmentStatus,
  AssessmentSupportProposal,
  AssessmentUnactivity,
  AssessmentWorkingTime,
  assessmentActivityKeyFromLabel,
  assessmentActivityLabels,
  assessmentActivitySort,
  assessmentChildAgeLabels,
  assessmentChildAgeSort,
  assessmentChildSituationLabels,
  assessmentChildSituationSort,
  assessmentContextLabels,
  assessmentContextSort,
  assessmentContractTypesLabels,
  assessmentContractTypesSort,
  assessmentExperienceLengthLabels,
  assessmentExperienceLengthSort,
  assessmentHowLongHasWorkedLabels,
  assessmentHowLongHasWorkedSort,
  assessmentJobCenterRegistrationLabels,
  assessmentJobCenterRegistrationSort,
  assessmentProExpDurationLabels,
  assessmentProExpDurationSort,
  assessmentStatusLabels,
  assessmentSupportProposalLabels,
  assessmentSupportProposalSort,
  assessmentUnactivityKeyFromLabel,
  assessmentUnactivityLabels,
  assessmentUnactivitySort,
  assessmentWorkingTimeLabels,
  assessmentWorkingTimeSort,
  residentialStatusLabels,
} from '../assessment.enums';
import { AssessmentService } from '../assessment.service';
import { AssessmentDto } from './assessment.dto';

// *********************************************************************************************
// * TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES *
// * TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES *
// *********************************************************************************************

type AssessmentFormGroup = FormGroup<{
  creationDateInterview: FormControl<Date>;
  status: FormControl<AssessmentStatus>;
  updateStatusDate: FormControl<Date>;
  context: FormControl<AssessmentContext>;

  // Sub forms
  detailForm: DetailFormGroup;
  globalSituationForm: GlobalSituationFormGroup;
  socialSituationForm: SocialSituationFormGroup;
  orientationForm: OrientationFormGroup;
  supportForm: SupportFormGroup;
}>;

type DetailFormGroup = FormGroup<{
  recipientNumber: FormControl<string>;
  title: FormControl<Title>;
  usualName: FormControl<string>;
  firstName: FormControl<string>;
  birthDate: FormControl<Date>;
  birthCity: FormControl<string>;
  nationality: FormControl<Nationality>;

  validIdentityDocument: FormControl<boolean>;
  renewalInProgress: FormControl<boolean>;
  renewalHelpNeeded: FormControl<boolean>;
  address: FormControl<string>;
  additionalAddress: FormControl<string>;
  city: FormControl<string>;
  postalCode: FormControl<string>;
  qpvId: FormControl<number>;

  landlineNumber: FormControl<string>;
  cellPhoneNumber: FormControl<string>;
  sms: FormControl<boolean>;
  emailAddress: FormControl<string>;
  email: FormControl<boolean>;

  maritalStatus: FormControl<MaritalStatus>;
  singleParent: FormControl<boolean>;
  partnerName: FormControl<string>;
  partnerSituation: FormControl<string>;

  childrenCount: FormControl<number>;
  children: FormArray<
    FormGroup<{
      age: FormControl<AssessmentChildAge>;
      situation: FormControl<AssessmentChildSituation>;
      comment: FormControl<string>;
      liveAtFamilyHome: FormControl<boolean>;
    }>
  >;
  childInProgress: FormControl<boolean>;
  isHelper: FormControl<boolean>;
  foreignParents: FormControl<'true' | 'false' | 'unknown'>;
  familyBarriers: FormControl<string>;
}>;

type GlobalSituationFormGroup = FormGroup<{
  claimRsaInsufRes: FormControl<boolean>;
  claimRsaNoRes: FormControl<boolean>;
  claimRsaNoMoreUnemployRights: FormControl<boolean>;
  claimRsaNoMoreUnemploy: FormControl<boolean>;
  claimRsaRefugeeClaimant: FormControl<boolean>;
  claimRsaOther: FormControl<boolean>;
  claimRsaOtherComment: FormControl<string>;

  isActive: FormControl<boolean>;
  activity: FormControl<AssessmentActivity>;
  unactivity: FormControl<AssessmentUnactivity>;

  accreditationSIAE: FormControl<boolean>;
  accreditationSIAEDate: FormControl<Date>;

  accreditationRQTH: FormControl<boolean>;
  accreditationRQTHDate: FormControl<Date>;

  accreditationAAH: FormControl<boolean>;
  accreditationAAHDate: FormControl<Date>;

  hasWorked: FormControl<boolean>;
  howLongHasWorked: FormControl<AssessmentHowLongHasWorked>;

  proExps: FormArray<
    FormGroup<{
      job: FormControl<string>;
      duration: FormControl<AssessmentProExpDuration>;
    }>
  >;

  searchingForJob: FormControl<boolean>;
  searchingForJobReasonNotTo: FormControl<string>;
  readyForNewJob: FormControl<boolean>;
  readyForNewJobReasonNotTo: FormControl<string>;
  jobCenterRegistration: FormControl<AssessmentJobCenterRegistration>;
  jobCenterLastUpdateDate: FormControl<Date>;
  integrationStepsTaken: FormControl<string>;

  degreeId: FormControl<number>;
  studyAbroad: FormControl<boolean>;
  degreeLabel: FormControl<string>;
  degreeGained: FormControl<boolean>;
  trainingAreaIds: FormControl<number[]>;

  formations: FormArray<
    FormGroup<{
      label: FormControl<string>;
      beginDate: FormControl<Date>;
      endDate: FormControl<Date>;
    }>
  >;
}>;

type SocialSituationFormGroup = FormGroup<{
  residentialStatus: FormControl<ResidentialStatus>;
  socialExclusion: FormControl<boolean>;
  indebtedness: FormControl<boolean>;

  assistance: FormControl<boolean>;
  assistedByConseiller: FormControl<boolean>;
  assistedByAssociation: FormControl<boolean>;
  assistedByTravailleurSocial: FormControl<boolean>;
  assistedByFamilleAmis: FormControl<boolean>;
  assistedByAutre: FormControl<boolean>;

  autonomousMobility: FormControl<boolean>;
  licenceB: FormControl<boolean>;
  tclSubscription: FormControl<boolean>;

  transportModes: FormArray<
    FormGroup<{
      transportModeId: FormControl<number>;
      description: FormControl<string>;
    }>
  >;

  maxMobilityDistance: FormControl<number>;
  maxMobilityGoToWorkTime: FormControl<number>;
}>;

type OrientationFormGroup = FormGroup<{
  hasProfessionnalProject: FormControl<boolean>;
  professionnalProjectDetails: FormControl<string>;
  contractSearchType: FormControl<AssessmentContractTypes>;
  workingTime: FormControl<AssessmentWorkingTime>;

  jobSoughts: FormArray<
    FormGroup<{
      famillyWantedJobType: FormControl<JobCategoryDto>;
      domainWantedJobType: FormControl<JobCategoryDto>;
      wantedJobType: FormControl<JobCategoryDto>;
      experienceLength: FormControl<AssessmentExperienceLength>;
    }>
  >;

  accessToFormation: FormControl<boolean>;
  housing: FormControl<boolean>;
  health: FormControl<boolean>;
  famillyOrganisation: FormControl<boolean>;
  mobility: FormControl<boolean>;
  budgetManagement: FormControl<boolean>;
  frenchUnderstanding: FormControl<boolean>;
  readingWritingDifficulties: FormControl<boolean>;
  adminDifficulties: FormControl<boolean>;
  numericDifficulties: FormControl<boolean>;
  noDifficulties: FormControl<boolean>;
  expectationsRegardingRSA: FormControl<string>;
}>;

type SupportFormGroup = FormGroup<{
  supportProposal: FormControl<AssessmentSupportProposal>;
  supportAcceptance: FormControl<boolean>;
  dataSharingAcceptance: FormControl<boolean>;
  supportChoiceExplanation: FormControl<string>;
}>;

// *********************************************************************************************
// * CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS *
// * CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS *
// *********************************************************************************************

@Component({
  selector: 'app-assessment-details',
  templateUrl: './assessment-details.component.html',
  styleUrls: ['./assessment-details.component.scss'],
})
export class AssessmentDetailsComponent {
  private readonly destroy$: Subject<void> = new Subject();

  public readonly StringUtils: StringUtils = StringUtils;

  public user: UserDto | undefined = undefined;
  public userCanUpdate: boolean = false;

  public assessment: AssessmentDto;
  public beneficiary: BeneficiaryDto;
  public beneficiaryComplement: BeneficiaryComplement;
  public beneficiaryEmployment: BeneficiaryEmploymentDto;

  // Forms
  public assessmentForm: AssessmentFormGroup;
  public detailForm: DetailFormGroup;
  public globalSituationForm: GlobalSituationFormGroup;
  public socialSituationForm: SocialSituationFormGroup;
  public orientationForm: OrientationFormGroup;
  public supportForm: SupportFormGroup;

  public submitted: boolean = false;

  // Form-arrays
  @ViewChild('children', { static: false }) public children: DynamicFormComponent;
  @ViewChild('proExps', { static: false }) public proExps: DynamicFormComponent;
  @ViewChild('formations', { static: false }) public formations: DynamicFormComponent;
  @ViewChild('transportModes', { static: false }) public transportModes: DynamicFormComponent;
  @ViewChild('jobSoughts', { static: false }) public jobSoughts: DynamicFormComponent;

  // List selects
  public readonly statusMap: Map<AssessmentStatus, string> = assessmentStatusLabels;

  public readonly contextMap: Map<AssessmentContext, string> = assessmentContextLabels;
  assessmentContextSort = assessmentContextSort;

  public readonly childAgeMap: Map<AssessmentChildAge, string> = assessmentChildAgeLabels;
  assessmentChildAgeSort = assessmentChildAgeSort;

  public readonly childSituationMap: Map<AssessmentChildSituation, string> = assessmentChildSituationLabels;
  assessmentChildSituationSort = assessmentChildSituationSort;

  public readonly activityMap: Map<AssessmentActivity, string> = assessmentActivityLabels;
  assessmentActivitySort = assessmentActivitySort;

  public readonly unactivityMap: Map<AssessmentUnactivity, string> = assessmentUnactivityLabels;
  assessmentUnactivitySort = assessmentUnactivitySort;

  public readonly howLongHasWorkedMap: Map<AssessmentHowLongHasWorked, string> = assessmentHowLongHasWorkedLabels;
  assessmentHowLongHasWorkedSort = assessmentHowLongHasWorkedSort;

  public readonly proExpDurationMap: Map<AssessmentProExpDuration, string> = assessmentProExpDurationLabels;
  assessmentProExpDurationSort = assessmentProExpDurationSort;

  public readonly jobCenterRegistrationMap: Map<AssessmentJobCenterRegistration, string> = assessmentJobCenterRegistrationLabels;
  assessmentJobCenterRegistrationSort = assessmentJobCenterRegistrationSort;

  public readonly experienceLengthMap: Map<AssessmentExperienceLength, string> = assessmentExperienceLengthLabels;
  assessmentExperienceLengthSort = assessmentExperienceLengthSort;

  public readonly contractTypesMap: Map<AssessmentContractTypes, string> = assessmentContractTypesLabels;
  assessmentContractTypesSort = assessmentContractTypesSort;

  public readonly workingTimeMap: Map<AssessmentWorkingTime, string> = assessmentWorkingTimeLabels;
  assessmentWorkingTimeSort = assessmentWorkingTimeSort;

  public readonly residentialStatusMap: Map<ResidentialStatusLabel, string> = residentialStatusLabels;

  public readonly supportProposalMap: Map<AssessmentSupportProposal, string> = assessmentSupportProposalLabels;
  assessmentSupportProposalSort = assessmentSupportProposalSort;

  public transportModeMap: DisplayLabel[];

  public qpvs: DisplayLabel[] = [];
  labelsByNationality = labelsByNationality;
  nationality = Nationality;
  maritalStatuses = MaritalStatus;

  public childrenFields: (string | FieldOptions)[] = [];
  public proExpsFields: (string | FieldOptions)[] = [];
  public formationsFields: (string | FieldOptions)[] = [];
  public transportModesFields: (string | FieldOptions)[] = [];
  public jobSoughtsFields: (string | FieldOptions)[] = [];

  public degrees: DisplayLabel[] = [];
  public trainingAreas: DisplayLabel[] = [];

  public sectors: JobCategoryDto[] = [];
  public employmentJobs: {
    famillyWantedJobType: JobCategoryDto;
    domainWantedJobType: JobCategoryDto;
    wantedJobType: JobCategoryDto;
    experienceLength: string;
  }[] = [];

  constructor(
    private readonly assessmentService: AssessmentService,
    private readonly authService: AuthService,
    private readonly beneficiaryService: BeneficiariesService,
    private readonly beneficiaryComplementsService: BeneficiaryComplementsService,
    private readonly beneficiaryEmploymentService: BeneficiaryEmploymentService,
    private readonly degreeService: DegreeService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly modalService: NgbModal,
    private readonly qpvService: QPVService,
    private readonly toastService: ToastService,
    private readonly structureService: StructureService,
    private readonly townService: TownService,
    private readonly trainingAreaService: TrainingAreaService,
    private readonly transportModeService: TransportModeService,
    private readonly jobService: JobService,
  ) {}

  public async onInit(beneficiaryId?: number, assessment?: AssessmentDto): Promise<void> {
    this.assessment = assessment ? assessment : new AssessmentDto();
    this.beneficiary = await this.beneficiaryService.findById(beneficiaryId);
    this.beneficiaryComplement = await this.beneficiaryComplementsService.findById(this.beneficiary.complementId);
    if (this.beneficiary.employmentId) {
      this.beneficiaryEmployment = await this.beneficiaryEmploymentService.findById(this.beneficiary.employmentId);
    }

    this.assessmentService.assessmentCreated$.pipe(takeUntil(this.destroy$)).subscribe((assessment: AssessmentDto) => {
      this.assessment = assessment;
    });

    // Get user and verify authorized actions
    this.user = await this.authService.getUser();
    this.userCanUpdate = this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+', 'TERRITORY'].includes(group));

    this.qpvs = await this.qpvService.findAll();
    this.sectors = await this.jobService.findAll();
    if (this.beneficiaryEmployment && !this.assessment.id) {
      this.beneficiaryEmployment.jobIds.forEach((jobId) => {
        (this.sectors || []).forEach((sector) => {
          (sector.subValues || []).forEach((domain) => {
            (domain.subValues || []).forEach((job) => {
              if (job.id === jobId) {
                this.employmentJobs.push({ famillyWantedJobType: sector, domainWantedJobType: domain, wantedJobType: job, experienceLength: null });
              }
            });
          });
        });
      });
    }
    await this.loadForm();

    this.degrees = await this.degreeService.findAll();
    this.trainingAreas = await this.trainingAreaService.findAll();

    this.transportModeMap = await this.transportModeService.findAll();

    this.childrenFields = this.getChildrenFields();
    this.proExpsFields = this.getProExpsFields();
    this.formationsFields = this.getFormationsFields();
    this.transportModesFields = this.getTransportModesFields();
    this.jobSoughtsFields = this.getjobSoughtsFields();
  }

  // *********************************************************************************************
  // * FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS *
  // * FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS *
  // *********************************************************************************************

  // *********************************************************************************************
  // * Global

  protected get fcAss() {
    return this.assessmentForm.controls;
  }

  protected get fvAss() {
    return this.assessmentForm.value;
  }

  protected get frAss() {
    return this.assessmentForm.getRawValue();
  }

  private async loadForm(): Promise<void> {
    this.loadDetailForm();
    this.loadGlobalSituationForm();
    this.loadSocialSituationForm();
    this.loadOrientationForm();
    this.loadSupportForm();
    this.assessmentForm = this.formBuilder.group({
      creationDateInterview: [
        this.assessment.creationDateInterview ? new Date(this.assessment.creationDateInterview) : new Date(),
        [Validators.required, DateValidators.dateFormatValidator],
      ],
      status: [this.assessment.status, [Validators.required]],
      updateStatusDate: [{ value: this.assessment.updateStatusDate ? new Date(this.assessment.updateStatusDate) : null, disabled: true }],
      context: [this.assessment.context, [Validators.required]],

      // Sub forms
      detailForm: this.detailForm,
      globalSituationForm: this.globalSituationForm,
      socialSituationForm: this.socialSituationForm,
      orientationForm: this.orientationForm,
      supportForm: this.supportForm,
    });

    if (!this.userCanUpdate) {
      this.assessmentForm.disable();
    }
  }

  public onStatusChange(): void {
    this.fcAss.updateStatusDate.setValue(
      this.assessment?.status !== this.fcAss.status.value ? new Date() : new Date(this.assessment.updateStatusDate),
    );
    this.assessmentForm.get('updateStatusDate').updateValueAndValidity();
  }

  // *********************************************************************************************
  // * Detail

  protected get fcDet() {
    return this.detailForm.controls;
  }

  protected get fvDet() {
    return this.detailForm.value;
  }

  protected get frDet() {
    return this.detailForm.getRawValue();
  }

  private loadDetailForm(): void {
    this.detailForm = this.formBuilder.group({
      recipientNumber: [{ value: this.beneficiary.recipientNumber, disabled: true }],
      title: [{ value: this.beneficiary.title, disabled: true }],
      usualName: [{ value: this.beneficiary.usualName, disabled: true }],
      firstName: [{ value: this.beneficiary.firstName, disabled: true }],
      birthDate: [{ value: this.beneficiary.birthDate, disabled: true }],
      birthCity: [{ value: this.beneficiary.birthCity, disabled: true }],
      nationality: [{ value: this.assessment.nationality ?? (this.beneficiary.nationality as Nationality), disabled: true }],

      validIdentityDocument: [this.assessment.validIdentityDocument],
      renewalInProgress: [this.assessment.renewalInProgress],
      renewalHelpNeeded: [this.assessment.renewalHelpNeeded],
      address: [{ value: this.assessment.address ?? this.beneficiary.residentialAddress.address, disabled: true }],
      additionalAddress: [{ value: this.assessment.additionalAddress ?? this.beneficiary.residentialAddress.additionalAddress, disabled: true }],
      city: [{ value: this.assessment.city ?? this.beneficiary.residentialAddress.city, disabled: true }],
      postalCode: [{ value: this.assessment.postalCode ?? this.beneficiary.residentialAddress.postalCode, disabled: true }],
      qpvId: [{ value: this.assessment.qpvId ?? this.beneficiary.residentialAddress.qpvId, disabled: true }],

      landlineNumber: [{ value: this.beneficiary.landlineNumber, disabled: true }],
      cellPhoneNumber: [{ value: this.beneficiary.cellPhoneNumber, disabled: true }],
      sms: [{ value: this.beneficiary.sms, disabled: true }],
      emailAddress: [{ value: this.beneficiary.emailAddress, disabled: true }],
      email: [{ value: this.beneficiary.email, disabled: true }],

      maritalStatus: [this.assessment.maritalStatus ?? this.beneficiary.maritalStatus, Validators.required],
      singleParent: [this.assessment.singleParent ?? this.beneficiaryComplement.singleParent],
      partnerName: [this.assessment.partnerName, [Validators.pattern(Utils.alphabeticalPattern), Validators.maxLength(30)]],
      partnerSituation: [this.assessment.partnerSituation, Validators.maxLength(30)],

      childrenCount: [this.beneficiaryComplement.childrenCount ?? this.assessment.childrenCount, Validators.pattern(Utils.integerPattern)],
      children: this.formBuilder.array(
        this.assessment.children.map((child) =>
          this.formBuilder.group({
            age: [child.age],
            situation: [child.situation],
            comment: [child.comment, Validators.maxLength(30)],
            liveAtFamilyHome: [child.liveAtFamilyHome],
          }),
        ),
      ),
      childInProgress: [this.assessment.childInProgress],
      isHelper: [this.assessment.isHelper],
      foreignParents: [this.assessment.foreignParents],
      familyBarriers: [this.assessment.familyBarriers],
    });

    this.fcDet.validIdentityDocument.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcDet.renewalInProgress.setValue(null);
      this.fcDet.renewalHelpNeeded.setValue(null);
    });
  }

  // *********************************************************************************************
  // * Detail - Children

  private getChildrenFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'age', validators: [Validators.required] },
      { value: 'situation', validators: [Validators.required] },
      { value: 'comment', validators: [Validators.maxLength(30)] },
      { value: 'liveAtFamilyHome', validators: [Validators.required] },
    ];
  }

  // *********************************************************************************************
  // * Global situation

  protected get fcGSit() {
    return this.globalSituationForm.controls;
  }

  protected get fvGSit() {
    return this.globalSituationForm.value;
  }

  protected get frGSit() {
    return this.globalSituationForm.getRawValue();
  }

  private loadGlobalSituationForm(): void {
    this.globalSituationForm = this.formBuilder.group(
      {
        claimRsaInsufRes: [this.assessment.claimRsaInsufRes],
        claimRsaNoRes: [this.assessment.claimRsaNoRes],
        claimRsaNoMoreUnemployRights: [this.assessment.claimRsaNoMoreUnemployRights],
        claimRsaNoMoreUnemploy: [this.assessment.claimRsaNoMoreUnemploy],
        claimRsaRefugeeClaimant: [this.assessment.claimRsaRefugeeClaimant],
        claimRsaOther: [this.assessment.claimRsaOther],
        claimRsaOtherComment: this.assessment.claimRsaOther
          ? [this.assessment.claimRsaOtherComment, Validators.maxLength(30)]
          : [{ value: null, disabled: true }],

        isActive: [this.assessment.isActive],
        activity: [this.assessment.activity],
        unactivity: [this.assessment.unactivity],

        accreditationSIAE: [this.assessment.accreditationSIAE ?? this.beneficiaryEmployment?.accreditationSIAE],
        accreditationSIAEDate: [
          (this.assessment.accreditationSIAEDate ?? this.beneficiaryEmployment?.accreditationSIAEDate)
            ? (this.assessment.accreditationSIAEDate ?? this.beneficiaryEmployment?.accreditationSIAEDate)
            : { value: null, disabled: true },
        ],

        accreditationRQTH: [this.assessment.accreditationRQTH ?? this.beneficiaryEmployment?.rqth],
        accreditationRQTHDate: [
          (this.assessment.accreditationRQTHDate ?? this.beneficiaryEmployment?.expiryRqthDate)
            ? (this.assessment.accreditationRQTHDate ?? this.beneficiaryEmployment?.expiryRqthDate)
            : { value: null, disabled: true },
        ],

        accreditationAAH: [this.assessment.accreditationAAH],
        accreditationAAHDate: [this.assessment.accreditationAAHDate ? this.assessment.accreditationAAHDate : { value: null, disabled: true }],

        hasWorked: [this.assessment.hasWorked],
        howLongHasWorked: [this.assessment.howLongHasWorked],

        proExps: this.formBuilder.array(
          this.assessment.proExps.map((proExp) =>
            this.formBuilder.group({
              job: [proExp.job],
              duration: [proExp.duration],
            }),
          ),
        ),

        searchingForJob: [this.assessment.searchingForJob],
        searchingForJobReasonNotTo: [this.assessment.searchingForJobReasonNotTo],
        readyForNewJob: [this.assessment.readyForNewJob],
        readyForNewJobReasonNotTo: [this.assessment.readyForNewJobReasonNotTo],
        jobCenterRegistration: [
          this.assessment.jobCenterRegistration ?? (this.beneficiary.lastUpdateDate ? AssessmentJobCenterRegistration.Yes : null),
        ],
        jobCenterLastUpdateDate: [
          this.assessment.jobCenterLastUpdateDate ??
            this.beneficiary.lastUpdateDate ?? {
              value: null,
              disabled: this.assessment.jobCenterRegistration || this.beneficiary.jobCenterNumber,
            },
        ],
        integrationStepsTaken: [this.assessment.integrationStepsTaken],

        degreeId: [this.assessment.degree?.id || this.beneficiaryEmployment?.degreeId],
        studyAbroad: [this.assessment.studyAbroad !== undefined ? this.assessment.studyAbroad : this.beneficiaryEmployment?.studyAbroad],
        degreeLabel: [this.assessment.degreeLabel],
        degreeGained: [this.assessment.degreeGained],
        trainingAreaIds: [this.assessment.trainingAreas?.map((trainingArea) => trainingArea.id) || this.beneficiaryEmployment?.trainingAreaIds],

        formations: this.formBuilder.array(
          this.assessment.formations.map((formation) =>
            this.formBuilder.group({
              label: [formation.label],
              beginDate: [formation.beginDate],
              endDate: [formation.endDate],
            }),
          ),
        ),
      },
      {
        // validators: [DateValidators.datesOrderValidator('beginDateLastJob', 'endDateLastJob')],
      },
    );

    this.fcGSit.claimRsaOther.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.claimRsaOtherComment.setValue(null);
      this.fcGSit.claimRsaOtherComment.setValidators(value ? [Validators.maxLength(30)] : []);
      this.fcGSit.claimRsaOtherComment[value ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.isActive.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.activity.setValue(null);
      this.fcGSit.unactivity.setValue(null);
    });

    this.fcGSit.accreditationSIAE.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.accreditationSIAEDate.setValue(null);
      this.fcGSit.accreditationSIAEDate[value ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.accreditationRQTH.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.accreditationRQTHDate.setValue(null);
      this.fcGSit.accreditationRQTHDate[value ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.accreditationAAH.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.accreditationAAHDate.setValue(null);
      this.fcGSit.accreditationAAHDate[value ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.hasWorked.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.howLongHasWorked.setValue(null);
    });

    this.fcGSit.searchingForJob.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.searchingForJobReasonNotTo.setValue(null);
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.readyForNewJob.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.readyForNewJobReasonNotTo.setValue(null);
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.jobCenterRegistration.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: AssessmentJobCenterRegistration) => {
      this.fcGSit.jobCenterLastUpdateDate.setValue(
        assessmentJobCenterRegistrationLabels.get(value) === AssessmentJobCenterRegistration.Yes ? this.beneficiary.lastUpdateDate : null,
      );
      this.fcGSit.jobCenterLastUpdateDate[
        assessmentJobCenterRegistrationLabels.get(value) === AssessmentJobCenterRegistration.Yes ? 'enable' : 'disable'
      ]();
      this.globalSituationForm.updateValueAndValidity();
    });
  }

  // *********************************************************************************************
  // * Global situation - Pro exps

  private getProExpsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'job', validators: [Validators.required] },
      { value: 'duration', validators: [Validators.required] },
    ];
  }

  // *********************************************************************************************
  // * Global situation - Formations

  private getFormationsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'label', validators: [Validators.required] },
      { value: 'beginDate', validators: [] },
      { value: 'endDate', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Social situation

  protected get fcSSit() {
    return this.socialSituationForm.controls;
  }

  protected get fvSSit() {
    return this.socialSituationForm.value;
  }

  protected get frSSit() {
    return this.socialSituationForm.getRawValue();
  }

  private loadSocialSituationForm(): void {
    this.socialSituationForm = this.formBuilder.group(
      {
        residentialStatus: [(this.assessment.residentialStatus ?? this.beneficiary.residentialAddress.residentialStatus) as ResidentialStatus],
        socialExclusion: [this.assessment.socialExclusion],
        indebtedness: [this.assessment.indebtedness],

        assistance: [this.assessment.assistance],
        assistedByConseiller: [this.assessment.assistedByConseiller],
        assistedByAssociation: [this.assessment.assistedByAssociation],
        assistedByTravailleurSocial: [this.assessment.assistedByTravailleurSocial],
        assistedByFamilleAmis: [this.assessment.assistedByFamilleAmis],
        assistedByAutre: [this.assessment.assistedByAutre],

        autonomousMobility: [this.assessment.autonomousMobility],
        licenceB: [this.assessment.licenceB],
        tclSubscription: [this.assessment.tclSubscription],

        transportModes: this.formBuilder.array(
          (this.assessment.transportModes?.length
            ? this.assessment.transportModes
            : this.beneficiaryEmployment?.transportModes.length
              ? this.beneficiaryEmployment.transportModes
              : []
          ).map((transportMode) =>
            this.formBuilder.group({
              transportModeId: [transportMode.transportModeId],
              description: [transportMode.description],
            }),
          ),
        ),

        maxMobilityDistance: [this.assessment.maxMobilityDistance, Validators.pattern(Utils.integerPattern)],
        maxMobilityGoToWorkTime: [this.assessment.maxMobilityGoToWorkTime, Validators.pattern(Utils.integerPattern)],
      },

      {
        // validators: [DateValidators.datesOrderValidator('beginDateLastJob', 'endDateLastJob')],
      },
    );

    this.fcSSit.assistance.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      if (!value) {
        this.fcSSit.assistedByConseiller.setValue(null);
        this.fcSSit.assistedByAssociation.setValue(null);
        this.fcSSit.assistedByTravailleurSocial.setValue(null);
        this.fcSSit.assistedByFamilleAmis.setValue(null);
        this.fcSSit.assistedByAutre.setValue(null);
        this.socialSituationForm.updateValueAndValidity();
      }
    });
  }

  // *********************************************************************************************
  // * Social situation - Transport modes

  private getTransportModesFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'transportModeId', validators: [] },
      { value: 'description', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Orientation

  protected get fcOri() {
    return this.orientationForm.controls;
  }

  protected get fvOri() {
    return this.orientationForm.value;
  }

  protected get frOri() {
    return this.orientationForm.getRawValue();
  }

  private loadOrientationForm(): void {
    this.initJobSoughtData();

    this.orientationForm = this.formBuilder.group({
      hasProfessionnalProject: [this.assessment.hasProfessionnalProject],
      professionnalProjectDetails: [this.assessment.professionnalProjectDetails],
      contractSearchType: [this.assessment.contractSearchType],
      workingTime: [this.assessment.workingTime],

      jobSoughts: this.formBuilder.array(
        this.assessment.jobSoughts.map((jobSought) =>
          this.formBuilder.group({
            famillyWantedJobType: [],
            domainWantedJobType: [],
            wantedJobType: [],
            experienceLength: [],
          }),
        ),
      ),

      accessToFormation: [this.assessment.accessToFormation],
      housing: [this.assessment.housing],
      health: [this.assessment.health],
      famillyOrganisation: [this.assessment.famillyOrganisation],
      mobility: [this.assessment.mobility],
      budgetManagement: [this.assessment.budgetManagement],
      frenchUnderstanding: [this.assessment.frenchUnderstanding],
      readingWritingDifficulties: [this.assessment.readingWritingDifficulties],
      adminDifficulties: [this.assessment.adminDifficulties],
      numericDifficulties: [this.assessment.numericDifficulties],
      noDifficulties: [this.assessment.noDifficulties],
      expectationsRegardingRSA: [this.assessment.expectationsRegardingRSA],
    });
  }

  private initJobSoughtData(): void {
    this.assessment.jobSoughts.map((jobSought) => {
      this.employmentJobs.push({
        famillyWantedJobType: this.sectors.find((sector) => sector.id === jobSought.famillyWantedJobTypeId),
        domainWantedJobType: this.sectors
          .find((sector) => sector.id === jobSought.famillyWantedJobTypeId)
          ?.subValues?.find((domain) => domain.id === jobSought.domainWantedJobTypeId),
        wantedJobType: this.sectors
          .find((sector) => sector.id === jobSought.famillyWantedJobTypeId)
          ?.subValues?.find((domain) => domain.id === jobSought.domainWantedJobTypeId)
          ?.subValues.find((job) => job.id === jobSought.wantedJobTypeId),
        experienceLength: jobSought.experienceLength,
      });
    });
  }

  // *********************************************************************************************
  // * Orientation - jobs soughts

  private getjobSoughtsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'famillyWantedJobType', validators: [] },
      { value: 'domainWantedJobType', validators: [] },
      { value: 'wantedJobType', validators: [] },
      { value: 'experienceLength', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Orientation - jobs soughts fields update

  public readonly handleSectorChange: (group: FormGroup) => void = (group: FormGroup): void => {
    group.controls.domainWantedJobType.setValue(undefined);
  };

  public readonly handleDomainChange: (group: FormGroup) => void = (group: FormGroup): void => {
    if (group.controls.domainWantedJobType.value?.subValues?.length === 1) {
      group.controls.wantedJobType.setValue(group.controls.domainWantedJobType.value?.subValues[0]);
    } else {
      group.controls.wantedJobType.setValue(undefined);
    }
  };

  // *********************************************************************************************
  // * Support

  protected get fcSup() {
    return this.supportForm.controls;
  }

  protected get fvSup() {
    return this.supportForm.value;
  }

  protected get frSup() {
    return this.supportForm.getRawValue();
  }

  private loadSupportForm(): void {
    this.supportForm = this.formBuilder.group({
      supportProposal: [this.assessment.supportProposal],
      supportAcceptance: [this.assessment.supportAcceptance],
      dataSharingAcceptance: [this.assessment.dataSharingAcceptance],
      supportChoiceExplanation: [this.assessment.supportChoiceExplanation],
    });
  }

  // *********************************************************************************************
  // * SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE *
  // * SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE *
  // *********************************************************************************************

  public async save(openFseSurveyModal: boolean): Promise<void> {
    this.submitted = true;

    if (
      !this.assessmentForm.valid ||
      !this.children.form.valid ||
      !this.proExps.form.valid ||
      !this.formations.form.valid ||
      !this.transportModes.form.valid ||
      !this.jobSoughts.form.valid
    ) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    } else {
      try {
        await this.assessmentService.saveAssessment({
          id: this.assessment.id,
          beneficiaryId: this.assessment.beneficiaryId || this.beneficiary.id,
          status: this.fvAss.status,
          creationDateInterview: moment.utc(this.fvAss.creationDateInterview).format('YYYY-MM-DD'),
          context: this.fvAss.context,

          // Details
          nationality: this.frDet.nationality || null,
          validIdentityDocument: this.fvDet.validIdentityDocument,
          renewalInProgress: this.fvDet.renewalInProgress,
          renewalHelpNeeded: this.fvDet.renewalHelpNeeded,
          address: this.frDet.address,
          additionalAddress: this.frDet.additionalAddress || null,
          city: this.frDet.city,
          postalCode: this.frDet.postalCode,
          qpvId: this.frDet.qpvId,

          // Familial situation
          maritalStatus: this.fvDet.maritalStatus,
          singleParent: this.fvDet.singleParent,
          partnerName: this.fvDet.partnerName || null,
          partnerSituation: this.fvDet.partnerSituation || null,

          // Children
          childrenCount: this.fvDet.childrenCount,
          children: this.children.form.controls.items.value.map((value) => ({
            id: value.id,
            age: value.age,
            situation: value.situation,
            comment: value.comment || null,
            liveAtFamilyHome: value.liveAtFamilyHome,
          })),
          childInProgress: this.fvDet.childInProgress,
          isHelper: this.fvDet.isHelper,
          foreignParents: this.fvDet.foreignParents || null,
          familyBarriers: this.fvDet.familyBarriers || null,

          // Global situation
          claimRsaInsufRes: this.fvGSit.claimRsaInsufRes,
          claimRsaNoRes: this.fvGSit.claimRsaNoRes,
          claimRsaNoMoreUnemployRights: this.fvGSit.claimRsaNoMoreUnemployRights,
          claimRsaNoMoreUnemploy: this.fvGSit.claimRsaNoMoreUnemploy,
          claimRsaRefugeeClaimant: this.fvGSit.claimRsaRefugeeClaimant,
          claimRsaOther: this.fvGSit.claimRsaOther,
          claimRsaOtherComment: this.fvGSit.claimRsaOtherComment || null,

          isActive: this.fvGSit.isActive,
          activity: this.fvGSit.activity,
          unactivity: this.fvGSit.unactivity,

          accreditationSIAE: this.fvGSit.accreditationSIAE,
          accreditationSIAEDate: this.fvGSit.accreditationSIAEDate ?? null,

          accreditationRQTH: this.fvGSit.accreditationRQTH,
          accreditationRQTHDate: this.fvGSit.accreditationRQTHDate ?? null,

          accreditationAAH: this.fvGSit.accreditationAAH,
          accreditationAAHDate: this.fvGSit.accreditationAAHDate ?? null,

          hasWorked: this.fvGSit.hasWorked,
          howLongHasWorked: this.fvGSit.howLongHasWorked,

          // Job
          proExps: this.proExps.form.controls.items.value.map((value) => ({
            id: value.id,
            job: value.job || null,
            duration: value.duration,
          })),
          searchingForJob: this.fvGSit.searchingForJob,
          searchingForJobReasonNotTo: this.fvGSit.searchingForJobReasonNotTo || null,
          readyForNewJob: this.fvGSit.readyForNewJob,
          readyForNewJobReasonNotTo: this.fvGSit.readyForNewJobReasonNotTo || null,
          jobCenterRegistration: this.fvGSit.jobCenterRegistration,
          jobCenterLastUpdateDate: this.fvGSit.jobCenterLastUpdateDate,
          integrationStepsTaken: this.fvGSit.integrationStepsTaken || null,

          // Degree
          degree: this.fvGSit.degreeId ? { id: this.fvGSit.degreeId } : null,
          studyAbroad: this.fvGSit.studyAbroad,
          degreeLabel: this.fvGSit.degreeLabel || null,
          degreeGained: this.fvGSit.degreeGained,
          trainingAreas: this.fvGSit.trainingAreaIds ? this.fvGSit.trainingAreaIds.map((id) => ({ id: id })) : null,

          formations: this.formations.form.controls.items.value.map((value) => ({
            id: value.id,
            label: value.label || null,
            beginDate: value.beginDate,
            endDate: value.endDate,
          })),

          // Social situation
          residentialStatus: this.fvSSit.residentialStatus || null,
          socialExclusion: this.fvSSit.socialExclusion ?? null,
          indebtedness: this.fvSSit.indebtedness ?? null,

          assistance: this.fvSSit.assistance ?? null,
          assistedByConseiller: this.fvSSit.assistedByConseiller,
          assistedByAssociation: this.fvSSit.assistedByAssociation,
          assistedByTravailleurSocial: this.fvSSit.assistedByTravailleurSocial,
          assistedByFamilleAmis: this.fvSSit.assistedByFamilleAmis,
          assistedByAutre: this.fvSSit.assistedByAutre,

          autonomousMobility: this.fvSSit.autonomousMobility,
          licenceB: this.fvSSit.licenceB,
          tclSubscription: this.fvSSit.tclSubscription,

          transportModes: this.transportModes.form.controls.items.value.map((value) => ({
            id: value.id,
            transportModeId: value.transportModeId || null,
            description: value.description || null,
          })),

          maxMobilityDistance: this.fvSSit.maxMobilityDistance || null,
          maxMobilityGoToWorkTime: this.fvSSit.maxMobilityGoToWorkTime || null,

          // Orientation
          hasProfessionnalProject: this.fvOri.hasProfessionnalProject,
          professionnalProjectDetails: this.fvOri.professionnalProjectDetails || null,
          contractSearchType: this.fvOri.contractSearchType || null,
          workingTime: this.fvOri.workingTime || null,

          jobSoughts: this.jobSoughts.form.controls.items.value.map((value) => ({
            id: value.id,
            famillyWantedJobTypeId: value.famillyWantedJobType.id || null,
            domainWantedJobTypeId: value.domainWantedJobType.id || null,
            wantedJobTypeId: value.wantedJobType.id || null,
            experienceLength: value.experienceLength || null,
          })),

          accessToFormation: this.fvOri.accessToFormation,
          housing: this.fvOri.housing,
          health: this.fvOri.health,
          famillyOrganisation: this.fvOri.famillyOrganisation,
          mobility: this.fvOri.mobility,
          budgetManagement: this.fvOri.budgetManagement,
          frenchUnderstanding: this.fvOri.frenchUnderstanding,
          readingWritingDifficulties: this.fvOri.readingWritingDifficulties,
          adminDifficulties: this.fvOri.adminDifficulties,
          numericDifficulties: this.fvOri.numericDifficulties,
          noDifficulties: this.fvOri.noDifficulties,
          expectationsRegardingRSA: this.fvOri.expectationsRegardingRSA || null,

          // Support
          supportProposal: this.fvSup.supportProposal || null,
          supportAcceptance: this.fvSup.supportAcceptance,
          dataSharingAcceptance: this.fvSup.dataSharingAcceptance,
          supportChoiceExplanation: this.fvSup.supportChoiceExplanation || null,
        } as AssessmentDto);

        this.toastService.show('Le diagnostic a été enregistré avec succès.', 'success');

        if (openFseSurveyModal) {
          await this.openFseSurveyModal(
            this.frAss.updateStatusDate.getFullYear().toString(),
            this.user?.groups.includes('STRUCTURE')
              ? this.user.structureId
              : (await this.structureService.findAllByIdIodas('METROPOLE DE LYON'))[0]?.id,
            moment.utc(this.fvAss.creationDateInterview).format('YYYY-MM-DD'),
          );
        }

        this.modal.close();
      } catch (error) {
        if (error.status === 440) {
          this.toastService.show('Veuillez enregistrer une signature dans votre profil.', 'danger');
        }
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.modal.close();
      await this.assessmentService.delete(this.assessment.id);
    }
  }

  public async duplicate(): Promise<void> {
    const newAssesment = { ...this.assessment };
    newAssesment.id = null;
    newAssesment.status = null;
    newAssesment.updateStatusDate = null;
    newAssesment.context = null;
    newAssesment.creationDateInterview = moment.utc(new Date()).format('YYYY-MM-DD');
    newAssesment.generatedSurveyFse = null;

    const modal: NgbModalRef = this.modalService.open(AssessmentDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: AssessmentDetailsComponent = modal.componentInstance as AssessmentDetailsComponent;
    await modalComponent.onInit(this.beneficiary.id, newAssesment);
    this.cancel();
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce diagnostic ?');
  }

  public async syncAssessment(): Promise<void> {
    const assessmentInit: AssessmentDto = await this.assessmentService.getInitByBeneficiaryId(this.assessment.beneficiaryId);
    // this.assessmentForm.controls.title.setValue(this.assessmentInit.title);
    // Etc.
  }

  // *********************************************************************************************
  // * Open FSE survey modal

  private async openFseSurveyModal(year: string, structureId: number, beginDate: string): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(FseDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: FseDetailsComponent = modal.componentInstance as FseDetailsComponent;

    const initialData: FseDetailInitialData = {
      year: year,
      structureId: structureId,
      beginDate: beginDate,
      assessmentSourceId: this.assessment.id,
      entryQuestion7: this.beneficiary.birthCity === 'Ville étrangère' ? '7No' : !this.beneficiary.birthCity ? undefined : '7Yes',
      entryQuestion8: '8d',
      entryQuestion3: null,
    } as FseDetailInitialData;

    if (this.assessment.isActive === true) {
      switch (assessmentActivityKeyFromLabel(this.assessment.activity)) {
        case AssessmentActivity.CasualEmployment:
          initialData.entryQuestion1 = '1cYes';
          break;

        case AssessmentActivity.SelfEmployed:
          initialData.entryQuestion1 = '1aYes';
          break;

        case AssessmentActivity.Farmer:
          initialData.entryQuestion1 = '1aYes';
          break;

        case AssessmentActivity.Employee:
          initialData.entryQuestion1 = '1bYes';
          break;
      }
    } else if (this.assessment.isActive === false) {
      initialData.entryQuestion1 = '1No';
      if (assessmentUnactivityKeyFromLabel(this.assessment.unactivity) === AssessmentUnactivity.Formation) {
        initialData.entryQuestion1e = '1eYes';
      }
    }

    switch (this.assessment.jobCenterRegistration) {
      case AssessmentJobCenterRegistration.Yes:
        initialData.entryQuestion9 = '9Yes';
        initialData.entryQuestion9a = this.assessment.jobCenterLastUpdateDate;
        break;

      case AssessmentJobCenterRegistration.No:
        initialData.entryQuestion9 = '9No';
        break;

      case AssessmentJobCenterRegistration.InProgress:
        initialData.entryQuestion9 = '9No';
        break;
    }

    if (this.assessment.degree?.id === 2) {
      initialData.entryQuestion2 = '2b';
    } else if ([3, 4].includes(this.assessment.degree?.id)) {
      initialData.entryQuestion2 = '2c';
    } else if ([5, 6, 7, 8].includes(this.assessment.degree?.id)) {
      initialData.entryQuestion2 = '2d';
    }

    if (this.assessment.foreignParents === 'true') {
      initialData.entryQuestion6 = '6Yes';
    } else if (this.assessment.foreignParents === 'false') {
      initialData.entryQuestion6 = '6No';
    } else if (this.assessment.foreignParents === 'unknown') {
      initialData.entryQuestion6 = '6DK';
    }

    if (this.assessment.accreditationRQTH || this.assessment.accreditationAAH) {
      initialData.entryQuestion3 = '3Yes';
    }

    initialData.entryQuestion4 = '4Yes';
    if (this.assessment.accreditationAAH) {
      initialData.entryQuestion4c = '4cYes';
    }

    if (this.assessment.socialExclusion === true) {
      initialData.entryQuestion5 = '5Yes';
    } else if (this.assessment.socialExclusion === false) {
      initialData.entryQuestion5 = '5No';
    } else if (this.assessment.socialExclusion === null) {
      initialData.entryQuestion5 = '5DK';
    }

    await modalComponent.onInit(undefined, initialData);
  }

  // *********************************************************************************************
  // * Generate document by opening signature modal

  public async generateDocument(): Promise<void> {
    const signatureData: string = await this.openAssessmentSignatureModal();
    await this.assessmentService.generateDocument(this.assessment.id, signatureData);
  }

  private async openAssessmentSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(AssessmentSignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: AssessmentSignatureComponent = modal.componentInstance as AssessmentSignatureComponent;
    modalComponent.assessment = this.assessment;
    return modalComponent.confirm();
  }

  // *********************************************************************************************
  // * BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY B *
  // * BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY BIRTH-CITY B *
  // *********************************************************************************************

  public inputFormat: (value: TownDto | string) => string = (value: TownDto | string): string =>
    value instanceof TownDto ? value.denomination : value;

  public resultFormat: (value: TownDto) => string = (value: TownDto): string => value.postalCode + ' - ' + value.denomination;

  public searchCity: (text$: Observable<string>) => Observable<TownDto[]> = (text$: Observable<string>): Observable<TownDto[]> =>
    text$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((text: string) => (text.length < 1 ? of([]) : this.townService.findByText(text))),
    );

  public onBlurCity(input: any): void {
    if (input && input.value === '') {
      this.fcDet.city.setValue('');
      this.fcDet.postalCode.setValue('');
    }
  }

  public onSelectCity(event: any, input: any): void {
    event.preventDefault();
    if (event?.item) {
      this.fcDet.city.setValue(event.item.denomination);
      this.fcDet.postalCode.setValue(event.item.postalCode);
      input.value = event.item.denomination;
    }
  }
}
